import React, { useRef } from "react";
import styles from "./OtpInput.module.css";

/**
 * Fixed-length numeric OTP input. Renders `length` cells (default 4),
 * auto-advances focus on input and backspace, and accepts paste of the
 * full code. Controlled via `value` (string) and `onChange(code)`.
 *
 * Set `error` to true to render the red-border error variant
 * (Figma node 1812-490).
 */
const OtpInput = ({
  length = 4,
  value = "",
  onChange,
  error = false,
  autoFocus = false,
  ariaLabel = "Enter OTP",
}) => {
  const refs = useRef([]);
  const cells = Array.from({ length }, (_, i) => value[i] || "");

  const setCell = (i, ch) => {
    const next = cells.slice();
    next[i] = ch;
    onChange?.(next.join(""));
  };

  const focusCell = (i) => {
    const el = refs.current[i];
    if (el) {
      el.focus();
      el.select?.();
    }
  };

  const handleChange = (i, e) => {
    const raw = e.target.value;
    // Last char wins so the user can overtype a filled cell.
    const ch = raw.slice(-1).replace(/\D/g, "");
    if (!ch) {
      setCell(i, "");
      return;
    }
    setCell(i, ch);
    if (i < length - 1) focusCell(i + 1);
  };

  const handleKeyDown = (i, e) => {
    if (e.key === "Backspace") {
      if (cells[i]) {
        setCell(i, "");
      } else if (i > 0) {
        focusCell(i - 1);
      }
      return;
    }
    if (e.key === "ArrowLeft" && i > 0) {
      e.preventDefault();
      focusCell(i - 1);
    }
    if (e.key === "ArrowRight" && i < length - 1) {
      e.preventDefault();
      focusCell(i + 1);
    }
  };

  const handlePaste = (i, e) => {
    const pasted = (e.clipboardData?.getData("text") || "").replace(/\D/g, "");
    if (!pasted) return;
    e.preventDefault();
    const next = cells.slice();
    for (let k = 0; k < pasted.length && i + k < length; k++) {
      next[i + k] = pasted[k];
    }
    onChange?.(next.join(""));
    const lastFilled = Math.min(i + pasted.length, length - 1);
    focusCell(lastFilled);
  };

  return (
    <div
      className={styles.row}
      role="group"
      aria-label={ariaLabel}
    >
      {cells.map((ch, i) => (
        <input
          key={i}
          ref={(el) => (refs.current[i] = el)}
          className={`${styles.cell} ${error ? styles.cellError : ""}`}
          type="text"
          inputMode="numeric"
          autoComplete={i === 0 ? "one-time-code" : "off"}
          maxLength={1}
          value={ch}
          onChange={(e) => handleChange(i, e)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={(e) => handlePaste(i, e)}
          onFocus={(e) => e.target.select()}
          autoFocus={autoFocus && i === 0}
          aria-label={`Digit ${i + 1} of ${length}`}
        />
      ))}
    </div>
  );
};

export default OtpInput;
